import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/components/SmoothScroll";
import { lineReveal, easeOutExpo } from "@/lib/motion";

const HERO_BG =
  "https://images.unsplash.com/photo-1709913472012-a0c243ca6cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxtb3RvcmN5Y2xlJTIwcmVwYWlyJTIwbWVjaGFuaWMlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzg0NDMwMTg5fDA&ixlib=rb-4.1.0&q=85";

const headline = [
  "We're building India's",
  "first mechanic-backed",
  "two-wheeler health",
  "ecosystem.",
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden px-6 md:px-12 pt-28 pb-20"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
        aria-hidden="true"
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-[#1A1D24]/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D24] via-[#1A1D24]/40 to-[#1A1D24]/70" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="max-w-7xl mx-auto w-full"
      >
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          data-testid="hero-prelaunch-label"
          className="text-xs font-bold uppercase tracking-[0.28em] text-[#E7B459] mb-6 flex items-center gap-3"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E7B459] animate-pulse" />
          Built for Indian riders. Coming soon.
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-[2.6rem] leading-[0.98] sm:text-6xl lg:text-7xl font-bold tracking-tighter max-w-5xl"
        >
          {headline.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                variants={lineReveal}
                initial="hidden"
                animate="show"
                transition={{
                  duration: 0.9,
                  ease: easeOutExpo,
                  delay: 0.35 + i * 0.11,
                }}
                className={`block ${i === 1 ? "text-[#D9544E]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          data-testid="hero-subtitle"
          className="mt-7 text-lg sm:text-xl leading-relaxed text-[#F4F1ED]/75 max-w-xl"
        >
          Think of a <span className="text-[#E7B459] font-medium">Zepto for
          parts</span> — only that we tell you what part needs replacement and
          which saves you the most money without cutting quality. We then
          deliver the part right to your nearby mechanic.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <button
            onClick={() => scrollToId("waitlist")}
            data-testid="hero-join-waitlist-btn"
            className="cta-glow group inline-flex items-center justify-center gap-2 rounded-full bg-[#D9544E] text-[#F4F1ED] text-base font-semibold px-8 py-4 hover:bg-[#c4423d] transition-colors duration-200"
          >
            Join the Waitlist
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </button>

          <button
            onClick={() => scrollToId("walkthrough")}
            data-testid="hero-see-how-link"
            className="inline-flex items-center justify-center gap-2 text-base font-medium text-[#F4F1ED]/80 hover:text-[#E7B459] transition-colors duration-200 px-2 py-2"
          >
            See how it works
            <span className="text-[#E7B459]">→</span>
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
